import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { StepUpDialog } from "../components/StepUpDialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import {
  ArrowDownLeft, ArrowUpRight, DollarSign, Send, Wallet, Clock,
  CheckCircle2, XCircle, Landmark, Users, TrendingUp, TrendingDown,
  Home, Building2, Shield, CreditCard, RefreshCw, Zap, AlertTriangle, ShieldOff,
  Loader2, Upload, FileText, Trash2, Download, Bell,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusBadge = (status) => {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    processing: "bg-blue-100 text-blue-800",
    posted: "bg-emerald-100 text-emerald-800",
    settled: "bg-emerald-100 text-emerald-800",
    completed: "bg-emerald-100 text-emerald-800",
    failed: "bg-red-100 text-red-800",
  };
  return map[status] || "bg-stone-100 text-stone-800";
};

const AdminPortal = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [allPayouts, setAllPayouts] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Fund dialog
  const [isFundOpen, setIsFundOpen] = useState(false);
  const [fundForm, setFundForm] = useState({ amount: "", description: "Ledger Funding" });

  // Linked-vendor payout dialog (uses real Plaid authorize+execute — no routing/account inputs)
  const [isPayoutOpen, setIsPayoutOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    recipient_id: "", amount: "", description: "Payout", network: "same-day-ach", express: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Documents management
  const [documents, setDocuments] = useState([]);
  const [docTitle, setDocTitle] = useState("");
  const [docCategory, setDocCategory] = useState("Vendor Agreement");
  const [docFile, setDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const pendingActionRef = useRef(null);
  const runReceipt = (p) => window.open(`${API_URL}/api/payouts/${p.payout_id}/receipt?auth=${localStorage.getItem("token")}`, "_blank", "noopener");

  // Fraud reversal dialog state
  const [fraudTarget, setFraudTarget] = useState(null); // {payout_id, amount, recipient_name, status}
  const [fraudForm, setFraudForm] = useState({ reason: "FRAUD", description: "", confirm: "" });
  const [isReversing, setIsReversing] = useState(false);
  const [simBusy, setSimBusy] = useState("");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [dashRes, llRes, payRes, txRes, docRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard`),
        axios.get(`${API_URL}/api/admin/vendors`),
        axios.get(`${API_URL}/api/admin/all-payouts`),
        axios.get(`${API_URL}/api/admin/all-transactions`),
        axios.get(`${API_URL}/api/documents`),
      ]);
      setDashboard(dashRes.data);
      setVendors(llRes.data.vendors);
      setAllPayouts(payRes.data.payouts);
      setAllTransactions(txRes.data.transactions);
      setDocuments(docRes.data.documents);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. Account Manager role required.");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const runSimulate = async (p, event) => {
    setSimBusy(p.payout_id);
    try {
      if (event === "retry") {
        const { data } = await axios.post(`${API_URL}/api/sandbox/transfer/retry`, { payout_id: p.payout_id });
        toast.success(`Retry created · status ${data.status}`);
      } else {
        const body = { payout_id: p.payout_id, event_type: event };
        if (event === "returned") body.ach_return_code = "R01";
        const { data } = await axios.post(`${API_URL}/api/sandbox/transfer/simulate`, body);
        toast.success(`Simulated ${event} · now ${data.payout_status}`);
      }
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simulation failed");
    } finally {
      setSimBusy("");
    }
  };

  const runMakeAvailable = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/sandbox/ledger/simulate-available`);
      toast.success(`Ledger funds made available · $${data.ledger?.available ?? "?"}`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Make-available failed");
    }
  };

  const handleFund = async () => {
    if (!fundForm.amount) { toast.error("Enter an amount"); return; }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/ledger/fund`, fundForm);
      const sweepId = res.data?.plaid_sweep_id;
      if (sweepId) {
        toast.success(`$${fundForm.amount} deposited via Plaid Ledger`, { description: `Sweep: ${sweepId.slice(0, 8)}…` });
      } else {
        toast.success(`$${fundForm.amount} recorded in CPC Ledger`, { description: "Plaid sweep unavailable" });
      }
      setIsFundOpen(false);
      setFundForm({ amount: "", description: "Ledger Funding" });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Funding failed"); }
    finally { setIsSubmitting(false); }
  };

  const handleDirectPayout = async () => {
    if (!payoutForm.recipient_id || !payoutForm.amount) {
      toast.error("Select a Plaid-linked recipient and enter an amount"); return;
    }
    setIsSubmitting(true);
    try {
      const networkSend = payoutForm.express ? "express" : payoutForm.network;
      const authRes = await axios.post(`${API_URL}/api/payouts/authorize`, {
        recipient_id: payoutForm.recipient_id,
        amount: payoutForm.amount,
        description: payoutForm.description,
        network: networkSend,
      });
      if (authRes.data?.is_mock) {
        toast.error("Authorization fell back to mock — recipient may not have a Plaid-linked bank");
        return;
      }
      const execRes = await axios.post(`${API_URL}/api/payouts/execute`, {
        payout_id: authRes.data.payout_id,
      });
      const transferId = execRes.data?.transfer_id;
      const networkUsed = authRes.data?.network_used;
      const fellBack = authRes.data?.express_fallback_used;
      let descParts = [];
      if (transferId) descParts.push(`Plaid Transfer · ${String(transferId).slice(0, 12)}…`);
      if (networkUsed === "rtp") descParts.push("RTP · settles in seconds");
      else if (networkUsed === "same-day-ach") descParts.push("Same-Day ACH · settles today");
      else if (networkUsed === "ach") descParts.push("ACH · 1–3 business days");
      if (fellBack) descParts.push("(RTP unavailable — fell back to Same-Day ACH)");
      toast.success(`$${payoutForm.amount} sent to ${authRes.data.recipient}`, {
        description: descParts.join(" · "),
      });
      setIsPayoutOpen(false);
      setPayoutForm({ recipient_id: "", amount: "", description: "Payout", network: "same-day-ach", express: false });
      fetchAll();
    } catch (e) {
      if (e.response?.data?.detail === "step_up_required") {
        pendingActionRef.current = handleDirectPayout;
        setStepUpOpen(true);
        return;
      }
      toast.error(e.response?.data?.detail || "Payout failed");
    } finally { setIsSubmitting(false); }
  };

  const handleSyncPlaid = async () => {
    setIsSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/sync-plaid-events`);
      const { events_processed, records_updated, error } = res.data;
      if (error) {
        toast.error(`Sync failed: ${error}`);
      } else if (events_processed === 0) {
        toast.success("Already up to date", { description: "No new Plaid events" });
      } else {
        toast.success(`Synced ${events_processed} Plaid events`, {
          description: `${records_updated} payout/funding records updated`,
        });
        fetchAll();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setIsSyncing(false); }
  };

  const handleRemind = async (vendor) => {
    const reminder_type = vendor.bank_linked ? "payment_pending" : "link_bank";
    try {
      const { data } = await axios.post(`${API_URL}/api/reminders`, { recipient_id: vendor.id, reminder_type });
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reminder");
    }
  };


  const handleResetMfa = async (userId, name) => {
    if (!window.confirm(`Reset two-factor authentication for ${name}? They will re-enrol on next login.`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/reset-mfa`, {});
      toast.success(`MFA reset for ${name}`, { description: "They will set up a new authenticator on next login." });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "step_up_required") {
        toast.error("For security, sign out and back in (fresh 2FA), then retry.");
      } else {
        toast.error(typeof detail === "string" ? detail : "MFA reset failed");
      }
    }
  };


  const handleUploadDocument = async () => {
    if (!docFile || !docTitle.trim()) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("title", docTitle.trim());
      fd.append("category", docCategory);
      await axios.post(`${API_URL}/api/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setDocTitle("");
      setDocFile(null);
      const { data } = await axios.get(`${API_URL}/api/documents`);
      setDocuments(data.documents);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (docId, title) => {
    if (!window.confirm(`Delete "${title}"? Vendors and employees will no longer see it.`)) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`);
      toast.success("Document deleted");
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const viewDocument = (doc) => {
    const token = localStorage.getItem("token");
    window.open(`${API_URL}/api/documents/${doc.id}/download?auth=${token}`, "_blank", "noopener");
  };


  const handleReverseFraud = async () => {
    if (!fraudTarget) return;
    if (fraudForm.confirm.trim().toUpperCase() !== "REVERSE") {
      toast.error('Type "REVERSE" to confirm the fraud reversal.');
      return;
    }
    setIsReversing(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/payouts/${fraudTarget.payout_id}/reverse-fraud`,
        { reason: fraudForm.reason, description: fraudForm.description }
      );
      toast.success(`Reversal initiated · refund ${res.data.refund_id.slice(0, 8)}…`, {
        description: `Plaid status: ${res.data.refund_status}`,
      });
      setFraudTarget(null);
      setFraudForm({ reason: "FRAUD", description: "", confirm: "" });
      fetchAll();
    } catch (e) {
      if (e.response?.data?.detail === "step_up_required") {
        pendingActionRef.current = handleReverseFraud;
        setStepUpOpen(true);
        return;
      }
      toast.error(e.response?.data?.detail || "Fraud reversal failed");
    } finally {
      setIsReversing(false);
    }
  };


  const [recipientCaps, setRecipientCaps] = useState(null);
  const [isCheckingCaps, setIsCheckingCaps] = useState(false);

  const checkRecipientCapabilities = async (recipientId) => {
    if (!recipientId) { setRecipientCaps(null); return; }
    setIsCheckingCaps(true);
    setRecipientCaps(null);
    try {
      const res = await axios.get(`${API_URL}/api/payouts/recipient-capabilities/${recipientId}`);
      setRecipientCaps(res.data);
    } catch (e) {
      setRecipientCaps({ error: e.response?.data?.detail || "Could not fetch capabilities" });
    } finally { setIsCheckingCaps(false); }
  };

  const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(parseFloat(v) || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

  if (isLoading) return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <Skeleton className="h-10 w-80" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-96" />
      </div>
    </Layout>
  );

  const ledger = dashboard?.ledger || {};
  const counts = dashboard?.counts || {};

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-emerald-700" />
              <Badge className="bg-emerald-100 text-emerald-800 border-0">Account Manager</Badge>
              {ledger?.ledger_source === "plaid" ? (
                <Badge className="bg-violet-100 text-violet-800 border-0" data-testid="plaid-ledger-live-badge">Plaid Ledger · Live</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800 border-0" data-testid="plaid-ledger-fallback-badge">DB Fallback</Badge>
              )}
            </div>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="admin-portal-title">
              CPC CFO Portal
            </h1>
            <p className="text-stone-500 mt-1">Manage ledger, transfers, and vendor payouts</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSyncPlaid}
              variant="ghost"
              disabled={isSyncing}
              className="rounded-full px-4 text-stone-700 hover:bg-stone-100"
              data-testid="admin-sync-plaid-btn"
              title="Sync payout statuses from Plaid Ledger"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isSyncing ? "animate-spin" : ""}`} />
              {isSyncing ? "Syncing…" : "Sync Plaid"}
            </Button>
            <Button onClick={runMakeAvailable} variant="ghost" className="rounded-full px-4 text-sky-800 hover:bg-sky-50" data-testid="admin-make-available-btn" title="Sandbox: convert pending ledger funds to available">
              <Zap className="w-4 h-4 mr-1" /> Make Available
            </Button>
            <Button onClick={() => setIsFundOpen(true)} variant="outline" className="rounded-full px-5 border-violet-300 text-violet-800 hover:bg-violet-50" data-testid="admin-fund-btn">
              <DollarSign className="w-4 h-4 mr-1" /> Fund Ledger
            </Button>
            <Button onClick={() => setIsPayoutOpen(true)} className="bg-emerald-900 hover:bg-emerald-800 rounded-full px-5" data-testid="admin-payout-btn">
              <Send className="w-4 h-4 mr-1" /> Send Payout
            </Button>
          </div>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="admin-stat-available">
            <CardContent className="p-5">
              <p className="text-sm text-stone-500">Ledger Available</p>
              <p className="text-2xl font-bold text-emerald-700">{fmt(ledger.available_balance)}</p>
              <p className="text-xs text-stone-400 mt-1">
                {ledger.ledger_source === "plaid" ? `Plaid · ${ledger.ledger_id?.slice(0, 8)}…` : "DB fallback"} · Pending: {fmt(ledger.pending_outflows)}
              </p>
            </CardContent>
          </Card>
          <Card data-testid="admin-stat-funded">
            <CardContent className="p-5">
              <p className="text-sm text-stone-500">Total Funded</p>
              <p className="text-2xl font-bold text-violet-700">{fmt(ledger.total_funding)}</p>
              <p className="text-xs text-stone-400 mt-1">Inflows: {fmt(ledger.total_inflows)}</p>
            </CardContent>
          </Card>
          <Card data-testid="admin-stat-payouts">
            <CardContent className="p-5">
              <p className="text-sm text-stone-500">Total Paid Out</p>
              <p className="text-2xl font-bold text-stone-700">{fmt(ledger.total_outflows)}</p>
              <p className="text-xs text-stone-400 mt-1">{counts.payouts} payouts</p>
            </CardContent>
          </Card>
          <Card data-testid="admin-stat-users">
            <CardContent className="p-5">
              <p className="text-sm text-stone-500">Platform Users</p>
              <p className="text-2xl font-bold text-blue-700">{(counts.vendors || 0) + (counts.employees || 0)}</p>
              <p className="text-xs text-stone-400 mt-1">{counts.vendors} vendors &middot; {counts.employees} employees</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview" data-testid="tab-overview">Vendors</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="tab-payouts">All Payouts</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
          </TabsList>

          {/* Vendor Directory */}
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Vendor Directory</CardTitle>
                <CardDescription>All vendors with bank status and payout history</CardDescription>
              </CardHeader>
              <CardContent>
                {vendors.length === 0 ? (
                  <div className="text-center py-10 text-stone-400"><Users className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No vendors registered</p></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Bank</TableHead>
                        <TableHead>Total Paid</TableHead>
                        <TableHead>Pending</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendors.map((ll) => (
                        <TableRow key={ll.id} data-testid={`vendor-row-${ll.id}`}>
                          <TableCell className="font-medium">{ll.full_name}</TableCell>
                          <TableCell className="text-stone-500">{ll.email}</TableCell>
                          <TableCell>
                            {ll.plaid_ready ? (
                              <Badge className="bg-emerald-100 text-emerald-800 border-0">{ll.bank_institution} ****{ll.bank_mask} · Plaid Ready</Badge>
                            ) : ll.bank_linked ? (
                              <Badge className="bg-amber-100 text-amber-800 border-0" title="Legacy link — needs backfill or re-link">{ll.bank_institution || "Bank"} ****{ll.bank_mask || "----"} · Not Plaid-ready</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 border-0">No Bank</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-semibold text-emerald-700">{fmt(ll.total_paid)}</TableCell>
                          <TableCell className="text-amber-700">{fmt(ll.pending_payouts)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full text-xs"
                              disabled={!ll.plaid_ready}
                              onClick={() => {
                                setPayoutForm({ ...payoutForm, recipient_id: ll.id, description: `Payout to ${ll.full_name}` });
                                setIsPayoutOpen(true);
                                checkRecipientCapabilities(ll.id);
                              }}
                              data-testid={`pay-vendor-${ll.id}`}
                              title={ll.plaid_ready ? "Send real Plaid credit transfer" : "Vendor must link their bank via production Plaid Link first"}
                            >
                              <Send className="w-3 h-3 mr-1" /> Pay
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-full text-xs text-stone-500 hover:text-red-600"
                              onClick={() => handleResetMfa(ll.id, ll.full_name)}
                              data-testid={`reset-mfa-${ll.id}`}
                              title="Reset this user's two-factor authentication"
                            >
                              <ShieldOff className="w-3 h-3 mr-1" /> Reset 2FA
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-full text-xs text-stone-500 hover:text-emerald-700"
                              onClick={() => handleRemind(ll)}
                              data-testid={`remind-${ll.id}`}
                              title={ll.bank_linked ? "Email a payment reminder" : "Email a reminder to link their bank"}
                            >
                              <Bell className="w-3 h-3 mr-1" /> Remind
                            </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Payouts */}
          <TabsContent value="payouts">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>All Payouts</CardTitle>
                <CardDescription>Complete payout history across the platform</CardDescription>
              </CardHeader>
              <CardContent>
                {allPayouts.length === 0 ? (
                  <div className="text-center py-10 text-stone-400"><Send className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No payouts yet</p></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Network</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allPayouts.map((p) => {
                        const reversible =
                          ["settled", "posted", "funds_available"].includes((p.status || "").toLowerCase()) &&
                          p.plaid_transfer_id &&
                          !String(p.plaid_transfer_id).startsWith("mock-") &&
                          !p.fraud_reversal_refund_id;
                        return (
                          <TableRow key={p.payout_id} data-testid={`admin-payout-${p.payout_id}`}>
                            <TableCell className="font-medium">
                              {p.recipient_name}
                              {p.is_direct && <span className="text-xs text-stone-400 block">ACH: {p.account_number_masked}</span>}
                            </TableCell>
                            <TableCell className="font-semibold text-emerald-700">{fmt(p.amount)}</TableCell>
                            <TableCell><Badge variant="outline" className="text-xs uppercase">{p.network}</Badge></TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <Badge className={`${statusBadge(p.status)} border-0`}>{p.status}</Badge>
                                {p.fraud_reversal_refund_id && (
                                  <Badge className="bg-red-100 text-red-800 border-0 text-[10px]" data-testid={`fraud-reversed-${p.payout_id}`}>
                                    Reversed · {p.fraud_reversal_status || "pending"}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-stone-500 text-sm">{p.description}</TableCell>
                            <TableCell className="text-stone-400 text-sm">{fmtDate(p.created_at)}</TableCell>
                            <TableCell className="text-right">
                              {p.plaid_transfer_id && !String(p.plaid_transfer_id).startsWith("mock-") && (
                                <Select value="" onValueChange={(v) => runSimulate(p, v)} disabled={simBusy === p.payout_id}>
                                  <SelectTrigger className="h-8 w-[130px] inline-flex mr-2 text-xs" data-testid={`simulate-trigger-${p.payout_id}`}>
                                    <Zap className="w-3.5 h-3.5 mr-1 text-sky-700" />
                                    <SelectValue placeholder={simBusy === p.payout_id ? "…" : "Simulate"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="posted" data-testid={`sim-posted-${p.payout_id}`}>Posted</SelectItem>
                                    <SelectItem value="settled" data-testid={`sim-settled-${p.payout_id}`}>Settled</SelectItem>
                                    <SelectItem value="failed" data-testid={`sim-failed-${p.payout_id}`}>Failed</SelectItem>
                                    <SelectItem value="returned" data-testid={`sim-returned-${p.payout_id}`}>Returned (R01)</SelectItem>
                                    <SelectItem value="retry" data-testid={`sim-retry-${p.payout_id}`}>Retry</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              <Button variant="ghost" size="sm" className="text-emerald-700 hover:text-emerald-800 mr-1" onClick={() => runReceipt(p)} data-testid={`receipt-btn-${p.payout_id}`} title="Download PDF receipt">
                                <FileText className="w-3.5 h-3.5 mr-1" /> Receipt
                              </Button>
                              {p.fraud_reversal_refund_id ? (
                                <span className="text-xs text-stone-400" data-testid={`fraud-done-${p.payout_id}`}>—</span>
                              ) : reversible ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                                  onClick={() => setFraudTarget({
                                    payout_id: p.payout_id,
                                    amount: p.amount,
                                    recipient_name: p.recipient_name,
                                    status: p.status,
                                  })}
                                  data-testid={`reverse-fraud-btn-${p.payout_id}`}
                                >
                                  <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                                  Reverse (Fraud)
                                </Button>
                              ) : (
                                <span
                                  className="text-xs text-stone-400"
                                  title={
                                    !p.plaid_transfer_id || String(p.plaid_transfer_id).startsWith("mock-")
                                      ? "No live Plaid transfer id"
                                      : `Status '${p.status}' is not eligible for reversal`
                                  }
                                  data-testid={`reverse-fraud-disabled-${p.payout_id}`}
                                >
                                  Not eligible
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Management */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Compliance Documents</CardTitle>
                <CardDescription>Upload agreements, ACH authorizations & policies — visible to all vendors and employees.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Upload form */}
                <div className="rounded-xl border border-stone-200 p-4 space-y-3 bg-stone-50/50">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Title</Label>
                      <Input value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="e.g. Vendor Agreement 2026" data-testid="doc-title-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Category</Label>
                      <Select value={docCategory} onValueChange={setDocCategory}>
                        <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Vendor Agreement", "ACH Authorization", "MFA Architecture", "Compliance", "Other"].map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">File (PDF, PNG or JPG — max 10 MB)</Label>
                    <Input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setDocFile(e.target.files[0])} data-testid="doc-file-input" />
                  </div>
                  <Button onClick={handleUploadDocument} disabled={uploadingDoc || !docFile || !docTitle.trim()} className="bg-emerald-900 hover:bg-emerald-800 rounded-full" data-testid="doc-upload-btn">
                    {uploadingDoc ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4 mr-2" /> Upload Document</>}
                  </Button>
                </div>

                {/* Document list */}
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-stone-400" data-testid="admin-documents-empty"><FileText className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No documents uploaded yet</p></div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between py-3 gap-3" data-testid={`admin-document-${doc.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4 text-emerald-700" /></div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-stone-900 truncate">{doc.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="secondary" className="text-[10px] bg-stone-100 text-stone-600">{doc.category}</Badge>
                              <span className="text-xs text-stone-400">{doc.original_filename}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => viewDocument(doc)} data-testid={`admin-doc-view-${doc.id}`}><Download className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteDocument(doc.id, doc.title)} data-testid={`admin-doc-delete-${doc.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Trail */}
          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Audit Trail</CardTitle>
                <CardDescription>Full ledger transaction history</CardDescription>
              </CardHeader>
              <CardContent>
                {allTransactions.length === 0 ? (
                  <div className="text-center py-10 text-stone-400"><DollarSign className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No transactions</p></div>
                ) : (
                  <div className="space-y-2">
                    {allTransactions.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors" data-testid={`admin-tx-${tx.id}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                            tx.type === "funding" ? "bg-violet-100" : tx.type === "inflow" ? "bg-emerald-100" : "bg-red-100"
                          }`}>
                            {tx.type === "funding" ? <Landmark className="w-4 h-4 text-violet-700" /> :
                             tx.type === "inflow" ? <ArrowDownLeft className="w-4 h-4 text-emerald-700" /> :
                             <ArrowUpRight className="w-4 h-4 text-red-700" />}
                          </div>
                          <div>
                            <p className="font-medium text-stone-900 text-sm">{tx.description}</p>
                            <p className="text-xs text-stone-400">{tx.counterparty} &middot; {tx.network.toUpperCase()} &middot; <Badge className={`${statusBadge(tx.status)} border-0 text-xs py-0`}>{tx.status}</Badge></p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold text-sm ${
                            tx.type === "outflow" ? "text-red-700" : tx.type === "funding" ? "text-violet-700" : "text-emerald-700"
                          }`}>
                            {tx.type === "outflow" ? "-" : "+"}{fmt(tx.amount)}
                          </p>
                          <p className="text-xs text-stone-400">{fmtDate(tx.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Fund Ledger Dialog */}
        <Dialog open={isFundOpen} onOpenChange={setIsFundOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Fund CPC Ledger</DialogTitle>
              <DialogDescription>Add funds from CPC's linked bank account</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Amount ($)</Label>
                <Input type="number" step="0.01" min="0.01" placeholder="10000.00" value={fundForm.amount} onChange={(e) => setFundForm({ ...fundForm, amount: e.target.value })} data-testid="admin-fund-amount" />
              </div>
              <div>
                <Label>Description</Label>
                <Input placeholder="Operating Capital - May" value={fundForm.description} onChange={(e) => setFundForm({ ...fundForm, description: e.target.value })} data-testid="admin-fund-desc" />
              </div>
              <Button onClick={handleFund} className="w-full bg-violet-700 hover:bg-violet-600 rounded-full" disabled={isSubmitting || !fundForm.amount} data-testid="admin-fund-confirm">
                {isSubmitting ? "Processing..." : `Fund ${fundForm.amount ? fmt(fundForm.amount) : "Ledger"}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Send Payout Dialog (Plaid-Linked Vendors Only) */}
        <Dialog open={isPayoutOpen} onOpenChange={setIsPayoutOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Send Payout</DialogTitle>
              <DialogDescription>
                Real Plaid credit transfer to a vendor's Plaid-linked bank. Auto-debits the Plaid Ledger.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Recipient (Plaid-linked vendors only)</Label>
                <Select
                  value={payoutForm.recipient_id}
                  onValueChange={(v) => {
                    setPayoutForm({ ...payoutForm, recipient_id: v });
                    checkRecipientCapabilities(v);
                  }}
                >
                  <SelectTrigger data-testid="admin-payout-recipient">
                    <SelectValue placeholder={vendors.filter(l => l.plaid_ready).length ? "Select a vendor…" : "No vendors are Plaid-ready yet"} />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.filter(l => l.plaid_ready).map((l) => (
                      <SelectItem key={l.id} value={l.id} data-testid={`admin-payout-recipient-${l.id}`}>
                        {l.full_name} · {l.bank_institution || "Bank"} ****{l.bank_mask || "----"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {vendors.filter(l => l.plaid_ready).length === 0 && (
                  <p className="text-xs text-amber-700 mt-2">
                    No vendors have a production Plaid-linked bank yet. Ask them to connect via the <b>Bank Account</b> page using real Plaid Link.
                  </p>
                )}
                {payoutForm.recipient_id && (
                  <div className="mt-2 flex flex-wrap gap-2 items-center" data-testid="recipient-capabilities-row">
                    {isCheckingCaps && (
                      <span className="text-xs text-stone-500 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Checking RTP/ACH support…</span>
                    )}
                    {!isCheckingCaps && recipientCaps?.error && (
                      <span className="text-xs text-red-700" data-testid="caps-error">⚠ {recipientCaps.error}</span>
                    )}
                    {!isCheckingCaps && recipientCaps?.supports && (
                      <>
                        <Badge className={recipientCaps.supports.rtp_credit ? "bg-violet-100 text-violet-800 border-0" : "bg-stone-100 text-stone-500 border-0"} data-testid="cap-rtp">
                          {recipientCaps.supports.rtp_credit ? "RTP Supported" : "RTP Not Supported"}
                        </Badge>
                        <Badge className={recipientCaps.supports.ach_credit ? "bg-emerald-100 text-emerald-800 border-0" : "bg-stone-100 text-stone-500 border-0"} data-testid="cap-ach">
                          {recipientCaps.supports.ach_credit ? "ACH Credit ✓" : "ACH Credit ✗"}
                        </Badge>
                        {recipientCaps.supports.wire_credit && (
                          <Badge className="bg-blue-100 text-blue-800 border-0" data-testid="cap-wire">Wire ✓</Badge>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-3 flex items-start gap-3" data-testid="express-payout-toggle-row">
                <Zap className={`w-5 h-5 mt-0.5 ${payoutForm.express ? "text-violet-700" : "text-stone-400"}`} />
                <div className="flex-1">
                  <Label className="text-sm font-medium text-stone-800">Express Payout</Label>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {payoutForm.express
                      ? "Tries RTP first (settles in seconds). Falls back to Same-Day ACH if recipient bank doesn't support RTP."
                      : "Use the network selector below."}
                  </p>
                </div>
                <Switch
                  checked={payoutForm.express}
                  onCheckedChange={(v) => setPayoutForm({ ...payoutForm, express: v })}
                  data-testid="express-payout-switch"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount ($)</Label>
                  <Input type="number" step="0.01" min="0.01" placeholder="2500.00" value={payoutForm.amount} onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })} data-testid="admin-payout-amount" />
                </div>
                <div>
                  <Label className={payoutForm.express ? "text-stone-400" : ""}>Network</Label>
                  <Select value={payoutForm.network} onValueChange={(v) => setPayoutForm({ ...payoutForm, network: v })} disabled={payoutForm.express}>
                    <SelectTrigger data-testid="admin-payout-network"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ach" data-testid="network-ach">ACH (1–3 business days)</SelectItem>
                      <SelectItem value="same-day-ach" data-testid="network-same-day-ach">Same-Day ACH</SelectItem>
                      <SelectItem value="rtp" data-testid="network-rtp">RTP (Real-Time Payments)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input placeholder="Invoice Payout - May 2026" value={payoutForm.description} onChange={(e) => setPayoutForm({ ...payoutForm, description: e.target.value })} data-testid="admin-payout-desc" />
              </div>
              {dashboard && <p className="text-xs text-stone-400">Available Plaid Ledger: {fmt(dashboard.ledger.available_balance)}</p>}
              <Button
                onClick={handleDirectPayout}
                className="w-full bg-emerald-900 hover:bg-emerald-800 rounded-full"
                disabled={isSubmitting || !payoutForm.recipient_id || !payoutForm.amount}
                data-testid="admin-payout-confirm"
              >
                {isSubmitting ? "Sending via Plaid…" : `Send ${payoutForm.amount ? fmt(payoutForm.amount) : "Payout"} via Plaid`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Fraud Reversal Confirmation Dialog */}
        <Dialog
          open={!!fraudTarget}
          onOpenChange={(open) => {
            if (!open) {
              setFraudTarget(null);
              setFraudForm({ reason: "FRAUD", description: "", confirm: "" });
            }
          }}
        >
          <DialogContent data-testid="fraud-reversal-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700" style={{ fontFamily: "Manrope, sans-serif" }}>
                <AlertTriangle className="w-5 h-5" />
                Reverse Payout for Fraud
              </DialogTitle>
              <DialogDescription>
                This initiates a Plaid <strong>refund/create</strong> against the original ACH transfer.
                The action is logged, billable, and cannot be undone. Plaid will issue an ACH credit back
                to the recipient&apos;s bank.
              </DialogDescription>
            </DialogHeader>
            {fraudTarget && (
              <div className="space-y-4">
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm">
                  <div className="flex justify-between"><span className="text-stone-600">Recipient</span><span className="font-medium">{fraudTarget.recipient_name}</span></div>
                  <div className="flex justify-between"><span className="text-stone-600">Amount</span><span className="font-semibold text-red-800">{fmt(fraudTarget.amount)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-600">Current status</span><Badge className={`${statusBadge(fraudTarget.status)} border-0 text-xs`}>{fraudTarget.status}</Badge></div>
                  <div className="flex justify-between"><span className="text-stone-600">Payout ID</span><span className="font-mono text-xs">{fraudTarget.payout_id.slice(0, 12)}…</span></div>
                </div>

                <div>
                  <Label>Reason</Label>
                  <Select value={fraudForm.reason} onValueChange={(v) => setFraudForm({ ...fraudForm, reason: v })}>
                    <SelectTrigger data-testid="fraud-reason-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FRAUD" data-testid="fraud-reason-fraud">FRAUD — unauthorized / fraudulent activity</SelectItem>
                      <SelectItem value="UNAUTHORIZED" data-testid="fraud-reason-unauthorized">UNAUTHORIZED — account holder did not authorize</SelectItem>
                      <SelectItem value="ERROR" data-testid="fraud-reason-error">ERROR — payout sent in error</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Notes (audit trail)</Label>
                  <Input
                    placeholder="Details about the fraud — case #, who reported, etc."
                    value={fraudForm.description}
                    onChange={(e) => setFraudForm({ ...fraudForm, description: e.target.value })}
                    maxLength={500}
                    data-testid="fraud-description-input"
                  />
                </div>

                <div>
                  <Label className="text-red-700">Type <span className="font-mono font-bold">REVERSE</span> to confirm</Label>
                  <Input
                    placeholder="REVERSE"
                    value={fraudForm.confirm}
                    onChange={(e) => setFraudForm({ ...fraudForm, confirm: e.target.value })}
                    className="border-red-200 focus-visible:ring-red-500"
                    data-testid="fraud-confirm-input"
                  />
                </div>

                <Button
                  onClick={handleReverseFraud}
                  className="w-full bg-red-700 hover:bg-red-800 rounded-full"
                  disabled={isReversing || fraudForm.confirm.trim().toUpperCase() !== "REVERSE"}
                  data-testid="fraud-confirm-btn"
                >
                  {isReversing ? "Reversing via Plaid…" : `Reverse ${fmt(fraudTarget.amount)} via Plaid`}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <StepUpDialog
          open={stepUpOpen}
          onOpenChange={setStepUpOpen}
          onVerified={() => {
            const fn = pendingActionRef.current;
            pendingActionRef.current = null;
            if (fn) fn();
          }}
        />
      </div>
    </Layout>
  );
};

export default AdminPortal;
